import * as React from "react"

import { Box, Flex } from "@chakra-ui/react"

import { range } from "../../lib/helpers/math"

import { Ellipsis, PagingNumberItem } from "./PagingNumberItem"

type Props = {
  pageCount: number
  selectedPage: number
  onPageClick: (page: number) => void
}

/**
 * Inspired by https://github.com/mui-org/material-ui/blob/v4.10.2/packages/material-ui-lab/src/Pagination/usePagination.js#L57
 * Please also take into account https://stackoverflow.com/a/62560497
 * @param pageCount
 * @param selectedPage
 * @param onPageClick
 * @constructor
 */
export function PagingNumbers({ pageCount, selectedPage, onPageClick }: Props) {
  const boundaryCount = 1
  const siblingCount = 1
  const startPages = range(1, Math.min(boundaryCount, pageCount))
  const endPages = range(
    Math.max(pageCount - boundaryCount + 1, boundaryCount + 1),
    pageCount,
  )
  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      selectedPage - siblingCount,
      // Lower boundary when page is high
      pageCount - boundaryCount - siblingCount * 2 - 1,
    ),
    // Greater than startPages
    boundaryCount + 2,
  )

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      selectedPage + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2,
    ),
    // Less than endPages
    endPages[0] - 2,
  )

  const items = [
    ...startPages,

    // Start ellipsis
    ...(siblingsStart > boundaryCount + 2
      ? ["start-ellipsis"]
      : boundaryCount + 1 < pageCount - boundaryCount
        ? [boundaryCount + 1]
        : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    ...(siblingsEnd < pageCount - boundaryCount - 1
      ? ["end-ellipsis"]
      : pageCount - boundaryCount > boundaryCount
        ? [pageCount - boundaryCount]
        : []),

    ...endPages,
  ]

  return (
    <Flex
      alignItems="flex-end"
      alignContent="flex-end"
      data-testid="pagingNumbers"
    >
      {items.map((item, i) => (
        <Box mr={3} key={`paging-item-${item}`}>
          {typeof item === "number" ? (
            <PagingNumberItem
              data-role={`paging-number-item-${i + 1}`}
              selected={item === selectedPage}
              onClick={() => onPageClick(item)}
            >
              {item.toString()}
            </PagingNumberItem>
          ) : (
            Ellipsis
          )}
        </Box>
      ))}
    </Flex>
  )
}
